/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { AppBar, Tab, Tabs } from "@mui/material";
import Box from "components/generals/Box";
import CaseTable from "components/tables/CaseTable";
import InsurancePatientTable from "components/tables/InsurancePatientTable";
import PharmacyPatientTable from "components/tables/PharmacyPatientTable";
import { CoreAPI_URLs } from "constants/http.const";
import useHttp from "hooks/http.hook";
import {
  HeaderEnum,
  PatientInsuranceInterface,
  PatientInterface,
  PatientPharmacyInterface,
  PharmacyInterface,
} from "@malmur-technologies/medicalsystem-shared";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectGeneralSettings } from "store/slices/generalSettingsSlice";

/**
 * Internal tab panel component for patient data tabs.
 * Controls visibility of tab content based on active tab.
 * 
 * @param props - Component props
 * @param props.children - Tab panel content
 * @param props.value - Currently active tab index
 * @param props.index - This panel's index
 * @returns Tab panel with conditional rendering
 */
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <Box sx={{ width: "100%" }}>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tab-panel-${index}`}
        aria-labelledby={`tab-${index}`}
        style={{ display: value === index ? "block" : "none" }}
      >
        {children}
      </div>
    </Box>
  );
};

/**
 * Props for TabsComponent.
 * @interface TabsComponentProps
 * @property {string} [patientId] - Patient ID to load data for
 * @property {Dispatch<SetStateAction<PatientPharmacyInterface[]>>} [setPharmacyRelationList] - State setter for pharmacy relations
 * @property {Dispatch<SetStateAction<Partial<PharmacyInterface>>>} [setDefaultPharmacy] - State setter for default pharmacy
 * @property {Dispatch<SetStateAction<PatientInsuranceInterface[]>>} [setInsuranceRelationList] - State setter for insurance relations
 * @property {Dispatch<SetStateAction<boolean>>} [setBoolNextButton] - State setter to enable/disable next button
 */
interface TabsComponentProps {
  patientId?: string;
  setPharmacyRelationList?: Dispatch<SetStateAction<PatientPharmacyInterface[]>>;
  setDefaultPharmacy?: Dispatch<SetStateAction<Partial<PharmacyInterface>>>;
  setInsuranceRelationList?: Dispatch<SetStateAction<PatientInsuranceInterface[]>>;
  setBoolNextButton?: Dispatch<SetStateAction<boolean>>;
}

/**
 * Tabbed interface for managing patient-related data.
 * Displays pharmacy, insurance, and case information in separate tabs.
 * 
 * Features:
 * - Three tabs: Pharmacy, Insurance, Cases
 * - Fetches patient data on mount
 * - Updates parent state with relation data
 * - Material-UI Tab components
 * - AppBar navigation
 * - Localized tab labels
 * 
 * Tabs:
 * 1. Pharmacy Tab: Patient pharmacy relationships and default pharmacy
 * 2. Insurance Tab: Patient insurance plans and coverage
 * 3. Cases Tab: Medical cases associated with patient
 * 
 * Data Flow:
 * - Loads patient data via API on mount if patientId provided
 * - Child tables manage their own data loading
 * - Updates parent component state via setter props
 * - Enables "Next" button when data is ready
 * 
 * @param patientId - ID of patient to display data for
 * @param setPharmacyRelationList - Updates parent with pharmacy list
 * @param setDefaultPharmacy - Updates parent with default pharmacy
 * @param setInsuranceRelationList - Updates parent with insurance list
 * @param setBoolNextButton - Controls parent next button state
 * @returns Tabbed patient data interface
 */
const TabsComponent: React.FC<TabsComponentProps> = ({
  patientId,
  setPharmacyRelationList,
  setDefaultPharmacy,
  setInsuranceRelationList,
  setBoolNextButton,
}) => {
  const [value, setValue] = useState(0);
  const { t } = useTranslation();
  const [_isLoading, _error, sendRequest] = useHttp();
  const [selectedPatient, setSelectedPatient] = useState<Partial<PatientInterface>>();
  const generalSettings = useSelector(selectGeneralSettings);

  // Determine which tabs should be visible
  const isInsuranceModuleEnabled = generalSettings?.isInsuranceModuleEnabled ?? true;
  const isPharmacyModuleEnabled = generalSettings?.isPharmacyModuleEnabled ?? true;

  useEffect(() => {
    if (patientId) {
      sendRequest(CoreAPI_URLs.PATIENTS_GENERAL_OPERATIONS, onGetPatient, "GET", undefined, [
        {
          header: HeaderEnum.PATIENT_ID,
          value: patientId,
        },
      ]);
    }
  }, []);

  const onGetPatient = (patient: PatientInterface) => {
    setSelectedPatient(patient);
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  //TODO: REMOVE MOCK DATA
  return (
    <Box sx={{ width: "100%" }}>
      <AppBar position="static">
        <Tabs value={value} onChange={handleChange} aria-label="tabs example">
          {isInsuranceModuleEnabled && <Tab id="cy-insurance-tab" label={t("patientTabPanel.insurance")} />}
          {isPharmacyModuleEnabled && <Tab id="cy-pharmacy-tab" label={t("patientTabPanel.pharmacies")} />}
          {selectedPatient?.id && <Tab id="cy-case-management-tab" label={t("patientTabPanel.caseManagement")} />}
        </Tabs>
      </AppBar>
      {isInsuranceModuleEnabled && (
        <TabPanel value={value} index={0}>
          <InsurancePatientTable
            patientSelected={selectedPatient || null}
            setInsuranceRelationList={setInsuranceRelationList || null}
            setBoolNextButton={setBoolNextButton || null}
          />
        </TabPanel>
      )}
      {isPharmacyModuleEnabled && (
        <TabPanel value={value} index={isInsuranceModuleEnabled ? 1 : 0}>
          <PharmacyPatientTable
            patientSelected={selectedPatient || null}
            setPharmacyRelationList={setPharmacyRelationList || null}
            setDefaultPharmacy={setDefaultPharmacy || null}
          />
        </TabPanel>
      )}
      {selectedPatient?.id && (
        <TabPanel
          value={value}
          index={
            (isInsuranceModuleEnabled ? 1 : 0) +
            (isPharmacyModuleEnabled ? 1 : 0)
          }
        >
          <CaseTable patientSelected={selectedPatient || null} />
        </TabPanel>
      )}
    </Box>
  );
};

export default TabsComponent;
